import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useMountEffect } from "primereact/hooks";
import { Field } from "react-final-form";
import Cookies from "js-cookie";
import {
  otpRequest,
  resetState,
  resendOTPRequest,
  verifyOTPRequest,
} from "../../../store/slices/authSlice";
import PreLoginContainer from "../../common/preLoginContainer";
import Form from "../../common/form";
import * as Validations from "../../common/form/validator";
import { OtpInput, TextInput } from "../../common/fields";
import { RouteConstants } from "../../../constants";
import Link from "../../common/link";
import { useLocation } from "react-router-dom";
import { errorText } from "../../../helpers/ErrorMessage";
import {
  CompanySignInRedirecting,
  useCompanySignInRedirect,
} from "../sso/CompanySignIn";

export const OtpLogin = ({
  action,
  error,
  otpRequest,
  requesting,
  resendOTPRequest,
  verifyOTPRequest,
  global,
  resetState,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const [isOTPsent, setIsOTPSent] = useState(false);
  const [isResentEnabled, setIsResentEnabled] = useState(false);
  const [timer, setTimer] = useState(60);
  // A code request sent from this page and not answered yet. The page moves
  // to the code step only once the request succeeds: it used to move at
  // once, so a refused request (for example the organisation switched off)
  // looked as if a code had been sent (E-BUG-3).
  const isCodeRequested = useRef(false);

  useMountEffect(() => {
    resetState();
  });

  const passwordOff = useCompanySignInRedirect(global?.enterprise);

  useEffect(() => {
    if (isOTPsent && !isResentEnabled) {
      setIsResentEnabled(false);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsResentEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOTPsent, isResentEnabled]);

  // On the code step a code request is a resend. The API sends at most one
  // new code a minute (R17): if it still finds it too soon, the wait starts
  // again at 60 seconds.
  useEffect(() => {
    if (
      isOTPsent &&
      action === "otp-request" &&
      !requesting &&
      error?.response === "OTP_REQUEST_TOO_SOON"
    ) {
      setTimer(60);
    }
  }, [isOTPsent, action, requesting, error]);

  useEffect(() => {
    if (isCodeRequested.current && action === "otp-request" && !requesting) {
      isCodeRequested.current = false;
      if (!error) {
        setIsOTPSent(true);
      }
    }
  }, [action, requesting, error]);

  const requestOtp = (values) => {
    if (!isOTPsent) {
      isCodeRequested.current = true;
      otpRequest({
        userName: values.username,
      });
    } else if (isOTPsent && values.otp.length === 6) {
      verifyOTPRequest({
        otp: values.otp,
      });
    }
  };

  if (passwordOff) {
    return <CompanySignInRedirecting enterprise={global?.enterprise} />;
  }

  return (
    <PreLoginContainer showLoginMessage={true}>
      <div className="title text-center">
        {intl.formatMessage({
          id: "sign_in_otp",
        })}
        <div className="tag-line text-muted">
          {intl.formatMessage({ id: "otp_link_message" })}
        </div>
      </div>
      <Form
        initialValues={{
          username: location?.state?.username || Cookies.get("username") || "",
        }}
        fullWidthButtons
        requesting={
          (action === "otp-request" || action === "verify-otp") && requesting
        }
        submitBtn={{
          label: intl.formatMessage({
            id: !isOTPsent ? "send_otp" : "otp_login",
          }),
          icon: "pi-arrow-right",
          onClick: (values) => requestOtp(values),
        }}
      >
        <div className="flex flex-column gap-2 mt-4">
          <Field
            name="username"
            component={TextInput}
            type="text"
            icon="pi-user"
            label={
              (isOTPsent &&
                intl.formatMessage({
                  id: "username",
                })) ||
              null
            }
            placeholder={intl.formatMessage({
              id: "username",
            })}
            disabled={isOTPsent}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
          {(isOTPsent && (
            <Field
              name="otp"
              component={OtpInput}
              label={intl.formatMessage({
                id: "otp",
              })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          )) ||
            null}
        </div>
        {/* Why a code request, a resend or the sign-in was refused. */}
        {(error &&
          !requesting &&
          (action === "verify-otp" || action === "otp-request") && (
            <div className="error-message">
              {errorText(error, intl)}
            </div>
          )) ||
          null}
        {(isOTPsent && !isResentEnabled && (
          <div className="mt-2">
            {timer > 0
              ? intl.formatMessage(
                  {
                    id: "otp_resend_timer",
                  },
                  { timer }
                )
              : null}
          </div>
        )) ||
          null}
        {(isOTPsent && isResentEnabled && (
          <div>
            <Link
              to="#"
              onClick={() => {
                resendOTPRequest();
                setIsResentEnabled(false);
                setTimer(120);
              }}
            >
              {intl.formatMessage({
                id: "resend_otp",
              })}
            </Link>
          </div>
        )) ||
          null}
      </Form>
      <div className="text-center mt-1">
        <Link to={RouteConstants.APP_ROUTE_LOGIN}>
          {intl.formatMessage({ id: "back_to_login" })}
        </Link>
      </div>
    </PreLoginContainer>
  );
};

const mapStateToProps = (state) => ({
  requesting: state.auth.requesting,
  error: state.auth.error,
  action: state.auth.action,
  global: state.global,
});

const mapDispatchToProps = (dispatch) => ({
  otpRequest: (data) => dispatch(otpRequest(data)),
  resetState: () => dispatch(resetState()),
  resendOTPRequest: (data) => dispatch(resendOTPRequest(data)),
  verifyOTPRequest: (data) => dispatch(verifyOTPRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OtpLogin);
